<template>
  <div class="_taskView__steps">
    <div class="_taskView__steps__header">
      <div class="_taskView__steps__title">
        <span>Steps List </span>
        <strong>{{ completedSteps.length }}/{{ sortedSteps.length }}</strong>
      </div>
    </div>
    <div class="_taskView__steps__list">
      <CaseTaskViewStepComponent 
        v-for="_step in sortedSteps"
        :key="_step.id"
        :step="_step"
        :isCompleteInOrder="isCompleteInOrder"
        :atStepUpdate="onStepUpdate"
        :isRefreshing="isRefreshing"
        :isVisible="true"
      ></CaseTaskViewStepComponent>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import Amplify, { Auth, API, graphqlOperation } from 'aws-amplify';
import * as mutations from '@/graphql/mutations';

import CaseTaskViewStepComponent from "@/components/DynamicForm/CaseTaskView/CaseTaskViewStepComponent";

export default {
  mixins: [],
  components: {
    CaseTaskViewStepComponent,
  },
  props: {
    task: {
      type: Object,
      required: true,
    },
    steps: {
      type: Array,
      required: true,
    },
    atStepUpdate: {
      type: Function,
      required: true,
    },
    isRefreshing: {
      type: Boolean,
      required: false,
    }
  },
  data: () => ({
    // 
  }),
  computed: {
    isCompleteInOrder() {
      return this.$reduce(this.task, 'isCompleteInOrder');
    },
    sortedSteps() {
      return _.sortBy(this.steps, 'order');
    },
    completedSteps() {
      return this.sortedSteps.filter(_step => 'complete' == _step.status.toLowerCase());
    }
  },
  async mounted() {
    this.getNextAvailableStep()
    window.$bus.$on('caseTaskView.forceUpdate', () => {
      setTimeout(() => {
        let steps = this.sortedSteps;
        if (this.isCompleteInOrder) {
          const firstAvailableStep = steps.find(_step => {
            return _step.status && 'complete' != _step.status.toLowerCase();
          });
          steps.map(_step => {
            _step.isAvailable = false;
          });
          if (firstAvailableStep) {
            firstAvailableStep.isAvailable = true;
          }
          this.steps = steps
        }
      }, 5000);
    });
  },
  methods: {
    /**
     * if steps should be completed in order - then fix invalid steps if any
     */
    async getNextAvailableStep() {
      if (this.isCompleteInOrder) {
        await this.disableAllSteps(this.sortedSteps)
          .then(response => {
            this.markNextAvailableStep(this.sortedSteps);
          })
          .catch(err => {
            console.log('Something is wrong with complete in order task stesps', this.steps);
          });
      }
    },
    /**
     * A step was just updated - setup next availble step
     * @param {Object} step - the step which was just updated
     * @returns {Void}
     */
    async onStepUpdate(step) {
      if (!this.isCompleteInOrder) {
        return this.atStepUpdate(step);
      }
      await this.updateStepsAvailability(step) 
        .then(response => {
          this.atStepUpdate(step);
        })
        .catch(err => {
          // 
        });
    },
    /**
     * Scan all steps
     * Mark any step which is complete as unavailable
     * Mark the first step which is not complete as available
     * @param {Object} step - the step which was just updated
     * @returns {Promise}
     */
    async updateStepsAvailability(step) {
      return new Promise(async (resolve, reject) => {
        let error = null;
        const steps = this.getUpdatedStepsList(step);
        
        await this.markNextAvailableStep(steps).catch(err => error = err);
        if (error) {
          return reject(error);
        }
        
        return resolve();
      });
    },
    /**
     * Scan steps and mark all steps where status = 'complete' as not available
     * @param {Array} steps - the list of steps
     * @returns {Promise}
     */
    async disableAllSteps(steps) {
      return new Promise(async (resolve, reject) => {
        let error = null;
        let firstOpenStep = null;
        
        for (let i = 0; i < steps.length; i++) {
          let _step = steps[i];
          
          // step is not available
          if (!_step.isAvailable) {
            continue;
          }
          
          // step is not complete and is available 
          if ('complete' != _step.status.toLowerCase() && _step.isAvailable && !firstOpenStep) {
            firstOpenStep = _step;
            continue;
          }
          
          // disable step
          const operation = graphqlOperation(mutations.updateTaskStep, {
            input: { id: _step.id, isAvailable: false }
          });
          await API.graphql(operation)
            .catch(err => {
              console.log('Error updating task step', err)
              error = err;
            });
          if (error) {
            return reject(error);
          }
        }
        // trigger after delay - to make sure notification arrives
        setTimeout(() => {
          resolve();
        }, 5000);
      });
    },
    /**
     * Find and update the recently updated step in the provided steps list
     * @param {Object} step - the step which was just updated
     * @returns {Array}
     */
    getUpdatedStepsList(step) {
      const steps = [...this.sortedSteps];
      const index = steps.findIndex(_step => _step.id == step.id);
      if (-1 != index) {
        steps[index] = step;
      }
      return steps;
    },
    /**
     * Find the next step which is not complete and mark as available
     * @param {Array} steps - the list of steps
     * @returns {Promise}
     */
    async markNextAvailableStep(steps) {
      return new Promise(async (resolve, reject) => {
        let error = null;
        for (let i = 0; i < steps.length; i++) {
          let _step = steps[i];
          if ('complete' != _step.status.toLowerCase()) {
            if (_step.isAvailable) {
              return resolve();
            }
            const operation = graphqlOperation(mutations.updateTaskStep, {
              input: { id: _step.id, isAvailable: true }
            });
            await API.graphql(operation)
              .catch(err => {
                console.log('Error updating task step', err)
                error = err;
              });
            if (error) {
              reject(error);
            }
            return resolve();
          }
        }
        return resolve();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
</style>
